import React, { useEffect, useRef, useState } from "react";
import * as semver from "semver";
import { add_toast_action, delete_old_toasts } from "common-tailwind-components";
import { useTranslation } from "react-i18next";

import { apply_update, check_for_updates, get_runtime_api_versions } from "../api";
import type { UpdateStatus } from "../api/types";
import { frontendCapabilities } from "../globals";
import store from "../store";
import useRecordingControls from "../utils/useRecordingControls";
import UpdateInstallModal from "./UpdateInstallModal";

const isUpdateStatus = (value: UpdateStatus | { status: string }): value is UpdateStatus =>
  "currentVersion" in value;

type VersionComparison = "newer" | "older" | "same" | "different";

const compareVersions = (leftVersion: string, rightVersion: string): VersionComparison => {
  if (!semver.valid(leftVersion) || !semver.valid(rightVersion)) {
    return leftVersion === rightVersion ? "same" : "different";
  }

  const comparison = semver.compare(leftVersion, rightVersion);
  if (comparison > 0) return "newer";
  if (comparison < 0) return "older";
  return "same";
};

/**
 * The app-lifetime update checks: the local-update check with its install
 * modal, and the API-version-mismatch toast. Mounted by the layout so they
 * run whatever page the app opens on.
 */
const UpdateChecks = () => {
  const { t } = useTranslation();
  const tRef = useRef(t);
  const [updateStatus, setUpdateStatus] = useState<UpdateStatus | null>(null);
  const [updateBusy, setUpdateBusy] = useState(false);
  const [updateError, setUpdateError] = useState<string | null>(null);
  const { recordingStatus, handleStopRecording } = useRecordingControls();
  const recordingMustStopBeforeUpdate =
    recordingStatus === "recording" || recordingStatus === "paused";

  useEffect(() => {
    tRef.current = t;
  }, [t]);

  useEffect(() => {
    if (!frontendCapabilities.localUpdates) {
      return;
    }

    check_for_updates()
      .then(setUpdateStatus)
      .catch((error: unknown) => {
        const message = error instanceof Error ? error.message : t("unable_to_check_updates");
        setUpdateError(message);
      })
      .finally(() => {
        setUpdateBusy(false);
      });
  }, [t]);

  useEffect(() => {
    if (!frontendCapabilities.localUpdates) {
      return;
    }

    let cancelled = false;
    get_runtime_api_versions()
      .then(({ core, companyRouter }) => {
        if (cancelled || core === companyRouter) {
          return;
        }

        const versionComparison = compareVersions(companyRouter, core);
        const relation =
          versionComparison === "newer"
            ? tRef.current("api_version_mismatch_remote_newer")
            : versionComparison === "older"
              ? tRef.current("api_version_mismatch_remote_older")
              : tRef.current("api_version_mismatch_remote_different");
        const toast = {
          title: tRef.current("api_version_mismatch_title"),
          message: tRef.current("api_version_mismatch_message", {
            localVersion: core,
            remoteVersion: companyRouter,
            relation,
          }),
          generating_object: "api-version-mismatch",
          store,
        };

        store.dispatch(delete_old_toasts(toast));
        store.dispatch(add_toast_action(toast));
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, []);

  const handleApply = async () => {
    setUpdateBusy(true);
    setUpdateError(null);
    try {
      if (recordingMustStopBeforeUpdate) {
        await handleStopRecording();
      }
      const result = await apply_update();
      if (isUpdateStatus(result)) {
        setUpdateStatus(result);
      }
    } catch (error: unknown) {
      setUpdateError(error instanceof Error ? error.message : t("update_failed"));
    } finally {
      setUpdateBusy(false);
    }
  };

  if (!frontendCapabilities.localUpdates) {
    return null;
  }

  return (
    <UpdateInstallModal
      status={updateStatus}
      busy={updateBusy || recordingStatus === "stopping"}
      error={updateError}
      requiresRecordingStop={recordingMustStopBeforeUpdate}
      onApply={() => {
        void handleApply();
      }}
    />
  );
};

export default UpdateChecks;
